import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '#app/utils/misc'
import {
	LANDING_ACCENT,
	SYSTEM_VIEWPORT_DELAY_MS,
} from './learning-system-explainer/constants'
import { LearningSystemVisual } from './learning-system-explainer/LearningSystemVisual'
import {
	type LearningSystemExplainerProps,
	resolveLearningSystemVisual,
} from './learning-system-explainer/types'

export type {
	LearningSystemExplainerItem,
	LearningSystemExplainerProps,
	LearningSystemExplainerVisual,
} from './learning-system-explainer/types'

const backgroundStyles = {
	default: 'bg-transparent',
	muted: 'bg-muted/20',
	'primary-light': 'bg-primary/5',
}

export function LearningSystemExplainer({
	eyebrow,
	title,
	subtitle,
	background = 'default',
	items = [],
}: LearningSystemExplainerProps) {
	const [activeIndex, setActiveIndex] = useState(0)
	const [systemPlayed, setSystemPlayed] = useState(false)
	const visualContainerRef = useRef<HTMLDivElement>(null)
	const uniqueId = useId().replace(/:/g, '')

	useEffect(() => {
		if (systemPlayed) return
		const node = visualContainerRef.current
		if (!node) return
		let timer: ReturnType<typeof setTimeout> | undefined
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting) {
					timer = setTimeout(
						() => setSystemPlayed(true),
						SYSTEM_VIEWPORT_DELAY_MS,
					)
					observer.disconnect()
				}
			},
			{ threshold: 0.35 },
		)
		observer.observe(node)
		return () => {
			observer.disconnect()
			if (timer) clearTimeout(timer)
		}
	}, [systemPlayed])

	if (items.length === 0) return null

	const resolvedActiveIndex =
		activeIndex >= 0 && activeIndex < items.length ? activeIndex : 0

	const handleSelect = (index: number) => {
		setActiveIndex(index)
		setSystemPlayed(true)
	}

	const activeItem = items[resolvedActiveIndex]
	const activeVisual = activeItem
		? resolveLearningSystemVisual(activeItem.visual, resolvedActiveIndex)
		: 'system'
	const shouldRenderActiveVisual = activeVisual !== 'system' || systemPlayed

	return (
		<section
			className={cn(
				'relative overflow-hidden px-6 py-28 sm:px-8 lg:px-12',
				backgroundStyles[background],
			)}
		>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0"
				style={{
					background:
						'linear-gradient(180deg, transparent 0%, var(--muted) 50%, transparent 100%)',
				}}
			/>

			<div className="relative mx-auto max-w-6xl">
				{(eyebrow || title || subtitle) && (
					<header className="mx-auto max-w-3xl text-center">
						{eyebrow ? (
							<p className="text-label text-muted-foreground">{eyebrow}</p>
						) : null}
						{title ? <h2 className="text-title-2 mt-4">{title}</h2> : null}
						{subtitle ? (
							<p className="text-muted-foreground mx-auto mt-5 max-w-2xl text-base leading-relaxed md:text-lg">
								{subtitle}
							</p>
						) : null}
					</header>
				)}

				<div className="mt-16 grid gap-8 lg:grid-cols-[380px_minmax(0,1fr)] lg:items-start lg:gap-12">
					<div className="border-primary/10 divide-primary/10 divide-y border-y">
						{items.map((item, index) => {
							const isActive = index === resolvedActiveIndex

							return (
								<button
									type="button"
									key={`${item.step}-${item.title}-${index}`}
									onClick={() => handleSelect(index)}
									aria-pressed={isActive}
									className="group flex w-full flex-col text-left"
								>
									<div className="flex items-center gap-4 py-5">
										<span
											className={cn(
												'h-2 w-2 shrink-0 rounded-full transition-all duration-300',
												!isActive && 'bg-primary/15',
											)}
											style={
												isActive
													? {
															backgroundColor: LANDING_ACCENT,
															boxShadow:
																'0 0 10px color-mix(in oklch, var(--gold) 45%, transparent)',
														}
													: undefined
											}
										/>
										<span className="text-muted-foreground font-mono text-xs">
											{item.step}
										</span>
										<span
											className={cn(
												'flex-1 text-[15px] font-semibold transition-colors duration-200',
												isActive
													? 'text-primary'
													: 'text-muted-foreground group-hover:text-muted-foreground',
											)}
										>
											{item.title}
										</span>
										<span
											className={cn(
												'text-muted-foreground shrink-0 font-mono text-sm transition-transform duration-200',
												isActive && 'rotate-45',
											)}
										>
											+
										</span>
									</div>
									<div
										className={cn(
											'grid transition-all duration-300 ease-in-out',
											isActive
												? 'grid-rows-[1fr] pb-5 opacity-100'
												: 'grid-rows-[0fr] opacity-0',
										)}
									>
										<div className="overflow-hidden">
											<div className="pr-4 pl-[3.5rem]">
												{item.eyebrow ? (
													<p className="text-label text-muted-foreground">
														{item.eyebrow}
													</p>
												) : null}
												<p className="text-body-sm text-muted-foreground mt-2.5">
													{item.description}
												</p>
												{item.detail ? (
													<p className="text-muted-foreground mt-3 text-[13px] leading-relaxed italic">
														{item.detail}
													</p>
												) : null}
											</div>
										</div>
									</div>
								</button>
							)
						})}
					</div>

					<div
						ref={visualContainerRef}
						className="relative flex min-h-[440px] items-center justify-center overflow-hidden rounded-[28px] border border-black/10 p-2 text-white sm:p-5"
						style={{
							backgroundColor: 'var(--landing-canvas)',
							boxShadow: 'var(--shadow-showcase)',
						}}
					>
						<div aria-hidden className="pointer-events-none absolute inset-0">
							<div
								className="absolute top-1/2 left-1/2 h-[340px] w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.07]"
								style={{
									background:
										'radial-gradient(circle, var(--gold), transparent 70%)',
								}}
							/>
						</div>
						<div className="relative h-[420px] w-full max-w-[560px]">
							{shouldRenderActiveVisual ? (
								<div
									key={`${activeVisual}-${resolvedActiveIndex}`}
									className="animate-in fade-in absolute inset-0 flex items-center justify-center duration-500"
								>
									<LearningSystemVisual
										visual={activeVisual}
										idPrefix={`${uniqueId}-${resolvedActiveIndex}`}
									/>
								</div>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
